import { IInput } from '../interfaces/IInput';

// Key prefix for edited input documents
const EDITED_INPUT_DOCUMENT_STORAGE_PREFIX = 'opterra_edited_input_document';
// Key prefix for original input documents
const ORIGINAL_INPUT_DOCUMENT_STORAGE_PREFIX =
  'opterra_original_input_document';

// Key prefix for each plan's expanded tasks on the Tasks grid (OP-2534)
const EXPANDED_TASKS_STORAGE_PREFIX = 'opterra_expanded_tasks';

const safeParse = <T>(value: string | null): T | null => {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

/**
 * Utilities for managing edited Input documents in browser storage.
 * Used to persist unsaved input drafts locally before/while syncing with the API.
 */
export const BrowserStorageUtils = {
  /**
   * Remember which tasks are expanded on a plan's Tasks grid. Session storage:
   * kept while switching plans and reloading, gone when the tab closes.
   */
  storeExpandedTaskIds: (inputId: string, taskIds: string[]): void => {
    try {
      sessionStorage.setItem(
        `${EXPANDED_TASKS_STORAGE_PREFIX}${inputId}`,
        JSON.stringify(taskIds)
      );
    } catch {
      // Unavailable storage just means the grid opens in its default state.
    }
  },

  /** The plan's remembered expanded tasks, or null when none are stored. */
  getExpandedTaskIds: (inputId: string): string[] | null => {
    try {
      const stored = safeParse<unknown>(
        sessionStorage.getItem(`${EXPANDED_TASKS_STORAGE_PREFIX}${inputId}`)
      );
      return Array.isArray(stored) &&
        stored.every((taskId) => typeof taskId === 'string')
        ? stored
        : null;
    } catch {
      return null;
    }
  },

  /**
   * Store an edited copy of an Input document.
   * This should be called before kicking off a background update.
   */
  storeEditedInput: (input: IInput): void => {
    try {
      localStorage.setItem(
        `${EDITED_INPUT_DOCUMENT_STORAGE_PREFIX}${input.id}`,
        JSON.stringify(input)
      );
    } catch (error) {
      console.error('Failed to store edited input in browser storage:', error);
    }
  },

  /**
   * Get an edited copy of an Input document.
   * Returns null if nothing is stored.
   */
  getEditedInput: (inputId: string): IInput | null => {
    try {
      const storedInput = localStorage.getItem(
        `${EDITED_INPUT_DOCUMENT_STORAGE_PREFIX}${inputId}`
      );
      return storedInput ? safeParse<IInput>(storedInput) : null;
    } catch (error) {
      console.error(
        'Failed to retrieve edited input from browser storage:',
        error
      );
      return null;
    }
  },
  /**
   * Store the original Input document before edits begin.
   */
  storeOriginalInput: (input: IInput): void => {
    try {
      localStorage.setItem(
        `${ORIGINAL_INPUT_DOCUMENT_STORAGE_PREFIX}${input.id}`,
        JSON.stringify(input)
      );
    } catch (error) {
      console.error(
        'Failed to store original input in browser storage:',
        error
      );
    }
  },

  /**
   * Get the original Input document stored before edits.
   */
  getOriginalInput: (inputId: string): IInput | null => {
    try {
      const storedInput = localStorage.getItem(
        `${ORIGINAL_INPUT_DOCUMENT_STORAGE_PREFIX}${inputId}`
      );
      return storedInput ? safeParse<IInput>(storedInput) : null;
    } catch (error) {
      console.error(
        'Failed to retrieve original input from browser storage:',
        error
      );
      return null;
    }
  },

  /**
   * Remove the stored original Input document.
   */
  removeOriginalInput: (inputId: string): void => {
    try {
      localStorage.removeItem(
        `${ORIGINAL_INPUT_DOCUMENT_STORAGE_PREFIX}${inputId}`
      );
    } catch (error) {
      console.error(
        'Failed to remove original input from browser storage:',
        error
      );
    }
  },

  /**
   * Remove an edited copy of an Input document.
   * Call this once the server update has succeeded.
   */
  removeEditedInput: (inputId: string): void => {
    try {
      localStorage.removeItem(
        `${EDITED_INPUT_DOCUMENT_STORAGE_PREFIX}${inputId}`
      );
    } catch (error) {
      console.error(
        'Failed to remove edited input from browser storage:',
        error
      );
    }
  },

  /**
   * Check if there is an edited copy of an Input document.
   */
  hasEditedInput: (inputId: string): boolean => {
    return BrowserStorageUtils.getEditedInput(inputId) !== null;
  },

  /**
   * Check whether an input has pending edits compared to its original snapshot.
   */
  hasPendingInputChanges: (inputId: string): boolean => {
    const edited = BrowserStorageUtils.getEditedInput(inputId);
    if (!edited) return false;
    const original = BrowserStorageUtils.getOriginalInput(inputId);
    if (!original) return true;
    return JSON.stringify(edited) !== JSON.stringify(original);
  },
};

export default BrowserStorageUtils;
